import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { tokenManager } from '../services/token-manager';
import { PANE_DEFAULT_WIDTH, PANE_MIN_WIDTH, PANE_MAX_WIDTH } from '../constants/paneLayout';
import { decodeBase64Url } from '../utils/token-utils';

// Types for the centralized store
export interface User {
  id: string; // ObjectId string from backend
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  isEmailVerified: boolean;
  isActive: boolean;
  mobile?: string; // User's mobile/phone number
  isMobileVerified?: boolean;
  phone?: string; // Alternative phone field (for compatibility)
  name?: string; // Full name (for compatibility)
  avatar?: string; // User's avatar URL
  profilePicture?: string; // User's profile picture URL (alias for avatar)
  timezone?: string; // User's timezone (e.g., 'America/New_York')
  timezoneOffset?: string; // Timezone offset (e.g., '-05:00')
  country?: string; // User's country
  city?: string; // User's city
  lastTimezoneUpdate?: Date; // Last time timezone was updated
  preferences?: Record<string, any>; // User preferences including notification settings
  createdAt: string;
  updatedAt: string;
  // Note: Business context removed - BusinessUser table handles this
  // Internal field for normalization - not part of the actual User type
  _id?: string; // MongoDB _id (will be normalized to id)
}

export interface AuthTokens {
  token: string;
  refreshToken: string;
}

export interface Country {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
}

export interface Subscription {
  id: string;
  planId: string;
  planCode: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  billingCycle: 'monthly' | 'quarterly' | 'yearly';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  services: {
    wabu?: {
      number_of_whatsapp_numbers: number;
      number_of_message_initiate: number;
      number_of_message_receive: number;
      text_messages: boolean;
      image_messages: boolean;
      video_messages: boolean;
      audio_messages: boolean;
      document_messages: boolean;
      template_messages: boolean;
      interactive_messages: boolean;
      overrideFooter?: boolean; // Allows this business to set a custom template footer
    };
    fbm?: {
      number_of_facebook_pages: number;
      number_of_message_initiate: number;
      number_of_message_receive: number;
      text_messages: boolean;
      image_messages: boolean;
      video_messages: boolean;
      audio_messages: boolean;
      document_messages: boolean;
      template_messages: boolean;
      interactive_messages: boolean;
      quick_replies: boolean;
      buttons: boolean;
      lists: boolean;
      carousels: boolean;
    };
    instagram?: any;
    twitter?: any;
    x?: any;
    users?: {
      max_users: number;
    };
    departments?: {
      max_departments: number;
    };
    storage?: {
      max_storage_gb: number;
    };
  };
}

export interface Business {
  _id: string; // ObjectId string from backend
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  website?: string;
  description?: string;
  industry?: string;
  size?: string;
  logo?: string;
  // Note: businessStatus removed - use status from BaseEntity instead
  isVerified: boolean;
  verificationDate?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  workingHours?: {
    monday?: { start: string; end: string; isWorking: boolean };
    tuesday?: { start: string; end: string; isWorking: boolean };
    wednesday?: { start: string; end: string; isWorking: boolean };
    thursday?: { start: string; end: string; isWorking: boolean };
    friday?: { start: string; end: string; isWorking: boolean };
    saturday?: { start: string; end: string; isWorking: boolean };
    sunday?: { start: string; end: string; isWorking: boolean };
  };
  socialMedia?: {
    whatsapp?: { number: string; isActive: boolean; lastSync?: string };
    facebook?: { pageId: string; isActive: boolean; lastSync?: string };
    instagram?: { username: string; isActive: boolean; lastSync?: string };
    twitter?: { username: string; isActive: boolean; lastSync?: string };
    linkedin?: { companyId: string; isActive: boolean; lastSync?: string };
  };
  ownerId?: string; // ObjectId string
  members: string[]; // Array of ObjectId strings
  departments: string[]; // Array of ObjectId strings
  subscriptionPlan?: string;
  subscriptionStatus?: 'active' | 'expired' | 'cancelled' | 'pending' | 'locked';
  subscriptionExpiry?: string;
  maxUsers?: number;
  maxChannels?: number;
  isLocked?: boolean;
  lockedReason?: 'subscription_expired' | 'manual' | 'payment_failed';
  lockedAt?: string;
  unlockedAt?: string;
  tags?: string[];
  notes?: string;
  customFields?: Record<string, any>;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export interface AppState {
  // User state
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pendingEmail: string;
  isNewUser: boolean;
  // Unified login: set once a phase-1 ("authenticated, no business selected yet") token is
  // minted by phone/Google/Meta login or email/password login, and cleared once
  // POST /auth/select-business (or business creation) promotes it to a full phase-2 token via
  // setAuthState. Deliberately NOT routed through `tokens`/`isAuthenticated` — the user isn't
  // "logged in" to the app proper until a business is selected.
  phase1Token: string | null;

  // 🔥 NEW: Auth state protection
  isAuthInitializing: boolean; // TRUE during login/token updates to prevent race conditions
  authInitializedAt: number | null; // Timestamp of last auth initialization
  authSource: 'login' | 'rehydration' | 'token-refresh' | null; // Source of current auth state
  
  // Business state
  currentBusiness: Business | null;
  userBusinesses: Business[];
  hasBusiness: boolean;
  isBusinessLoading: boolean;
  businessRole: 'owner' | 'admin' | 'member' | null; // User's role in current business
  
  // Subscription state
  currentSubscription: Subscription | null;
  isSubscriptionLoading: boolean;
  
  // Static data
  countries: Country[];

  // Global refresh tokens
  contactsRefreshToken: number;
  
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  updateTokens: (tokens: Partial<AuthTokens>) => void;
  setPendingEmail: (email: string) => void;
  setIsNewUser: (isNew: boolean) => void;
  setPhase1Token: (token: string | null) => void;
  setUserFromPhase1Token: (token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  initializeTokenManager: () => void;
  
  // 🔥 NEW: Unified auth management
  setAuthState: (authData: { tokens: AuthTokens; user: User; source: 'login' | 'rehydration' | 'token-refresh' }) => Promise<void>;
  updateAuthTokens: (newTokens: AuthTokens, source: 'business-creation' | 'service-setup' | 'token-refresh') => Promise<void>;
  validateBusinessState: () => boolean;
  
  // Business actions
  setCurrentBusiness: (business: Business | null) => void;
  setUserBusinesses: (businesses: Business[]) => void;
  setBusinessLoading: (loading: boolean) => void;
  checkUserBusinesses: () => Promise<boolean>;
  extractBusinessFromToken: (token: string) => void;
  
  // Subscription actions
  setCurrentSubscription: (subscription: Subscription | null) => void;
  setSubscriptionLoading: (loading: boolean) => void;
  loadCurrentSubscription: () => Promise<void>;

  // Refresh actions
  bumpContactsRefreshToken: () => void;

  // UI locale override (used on auth screens before login)
  uiLocale: string;
  setUiLocale: (locale: string) => void;

  // Light/dark theme preference — see src/contexts/ThemeContext.tsx and src/theme/colors.ts
  themeMode: 'light' | 'dark';
  setThemeMode: (mode: 'light' | 'dark') => void;

  // Desktop-web LeftPane/RightPane collapse + resize state — see
  // src/components/LeftPane.tsx, RightPane.tsx, ScreenLayout.tsx
  leftPaneCollapsed: boolean;
  leftPaneWidth: number;
  rightPaneCollapsed: boolean;
  rightPaneWidth: number;
  setLeftPaneCollapsed: (collapsed: boolean) => void;
  setLeftPaneWidth: (width: number) => void;
  setRightPaneCollapsed: (collapsed: boolean) => void;
  setRightPaneWidth: (width: number) => void;
}

// Initialize countries data — full world list sorted alphabetically
const initialCountries: Country[] = [
  { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', dialCode: '+93' },
  { code: 'AL', name: 'Albania', flag: '🇦🇱', dialCode: '+355' },
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿', dialCode: '+213' },
  { code: 'AD', name: 'Andorra', flag: '🇦🇩', dialCode: '+376' },
  { code: 'AO', name: 'Angola', flag: '🇦🇴', dialCode: '+244' },
  { code: 'AG', name: 'Antigua and Barbuda', flag: '🇦🇬', dialCode: '+1' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54' },
  { code: 'AM', name: 'Armenia', flag: '🇦🇲', dialCode: '+374' },
  { code: 'AW', name: 'Aruba', flag: '🇦🇼', dialCode: '+297' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '+43' },
  { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', dialCode: '+994' },
  { code: 'BS', name: 'Bahamas', flag: '🇧🇸', dialCode: '+1' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dialCode: '+973' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', dialCode: '+880' },
  { code: 'BB', name: 'Barbados', flag: '🇧🇧', dialCode: '+1' },
  { code: 'BY', name: 'Belarus', flag: '🇧🇾', dialCode: '+375' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', dialCode: '+32' },
  { code: 'BZ', name: 'Belize', flag: '🇧🇿', dialCode: '+501' },
  { code: 'BJ', name: 'Benin', flag: '🇧🇯', dialCode: '+229' },
  { code: 'BT', name: 'Bhutan', flag: '🇧🇹', dialCode: '+975' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴', dialCode: '+591' },
  { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦', dialCode: '+387' },
  { code: 'BW', name: 'Botswana', flag: '🇧🇼', dialCode: '+267' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
  { code: 'BN', name: 'Brunei', flag: '🇧🇳', dialCode: '+673' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', dialCode: '+359' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', dialCode: '+226' },
  { code: 'BI', name: 'Burundi', flag: '🇧🇮', dialCode: '+257' },
  { code: 'KH', name: 'Cambodia', flag: '🇰🇭', dialCode: '+855' },
  { code: 'CM', name: 'Cameroon', flag: '🇨🇲', dialCode: '+237' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', dialCode: '+238' },
  { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', dialCode: '+236' },
  { code: 'TD', name: 'Chad', flag: '🇹🇩', dialCode: '+235' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱', dialCode: '+56' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', dialCode: '+57' },
  { code: 'KM', name: 'Comoros', flag: '🇰🇲', dialCode: '+269' },
  { code: 'CG', name: 'Congo', flag: '🇨🇬', dialCode: '+242' },
  { code: 'CD', name: 'Congo (DR)', flag: '🇨🇩', dialCode: '+243' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', dialCode: '+506' },
  { code: 'CI', name: "Côte d'Ivoire", flag: '🇨🇮', dialCode: '+225' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷', dialCode: '+385' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺', dialCode: '+53' },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾', dialCode: '+357' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', dialCode: '+420' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', dialCode: '+45' },
  { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', dialCode: '+253' },
  { code: 'DM', name: 'Dominica', flag: '🇩🇲', dialCode: '+1' },
  { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', dialCode: '+1' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨', dialCode: '+593' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻', dialCode: '+503' },
  { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', dialCode: '+240' },
  { code: 'ER', name: 'Eritrea', flag: '🇪🇷', dialCode: '+291' },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪', dialCode: '+372' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', dialCode: '+251' },
  { code: 'FJ', name: 'Fiji', flag: '🇫🇯', dialCode: '+679' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', dialCode: '+358' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'GA', name: 'Gabon', flag: '🇬🇦', dialCode: '+241' },
  { code: 'GM', name: 'Gambia', flag: '🇬🇲', dialCode: '+220' },
  { code: 'GE', name: 'Georgia', flag: '🇬🇪', dialCode: '+995' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭', dialCode: '+233' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', dialCode: '+30' },
  { code: 'GD', name: 'Grenada', flag: '🇬🇩', dialCode: '+1' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹', dialCode: '+502' },
  { code: 'GN', name: 'Guinea', flag: '🇬🇳', dialCode: '+224' },
  { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', dialCode: '+245' },
  { code: 'GY', name: 'Guyana', flag: '🇬🇾', dialCode: '+592' },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹', dialCode: '+509' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳', dialCode: '+504' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', dialCode: '+852' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺', dialCode: '+36' },
  { code: 'IS', name: 'Iceland', flag: '🇮🇸', dialCode: '+354' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', dialCode: '+62' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷', dialCode: '+98' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶', dialCode: '+964' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', dialCode: '+353' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱', dialCode: '+972' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲', dialCode: '+1' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴', dialCode: '+962' },
  { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿', dialCode: '+7' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' },
  { code: 'KI', name: 'Kiribati', flag: '🇰🇮', dialCode: '+686' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '+965' },
  { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', dialCode: '+996' },
  { code: 'LA', name: 'Laos', flag: '🇱🇦', dialCode: '+856' },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻', dialCode: '+371' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dialCode: '+961' },
  { code: 'LS', name: 'Lesotho', flag: '🇱🇸', dialCode: '+266' },
  { code: 'LR', name: 'Liberia', flag: '🇱🇷', dialCode: '+231' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾', dialCode: '+218' },
  { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', dialCode: '+423' },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹', dialCode: '+370' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', dialCode: '+352' },
  { code: 'MO', name: 'Macau', flag: '🇲🇴', dialCode: '+853' },
  { code: 'MG', name: 'Madagascar', flag: '🇲🇬', dialCode: '+261' },
  { code: 'MW', name: 'Malawi', flag: '🇲🇼', dialCode: '+265' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dialCode: '+60' },
  { code: 'MV', name: 'Maldives', flag: '🇲🇻', dialCode: '+960' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱', dialCode: '+223' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹', dialCode: '+356' },
  { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭', dialCode: '+692' },
  { code: 'MR', name: 'Mauritania', flag: '🇲🇷', dialCode: '+222' },
  { code: 'MU', name: 'Mauritius', flag: '🇲🇺', dialCode: '+230' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52' },
  { code: 'FM', name: 'Micronesia', flag: '🇫🇲', dialCode: '+691' },
  { code: 'MD', name: 'Moldova', flag: '🇲🇩', dialCode: '+373' },
  { code: 'MC', name: 'Monaco', flag: '🇲🇨', dialCode: '+377' },
  { code: 'MN', name: 'Mongolia', flag: '🇲🇳', dialCode: '+976' },
  { code: 'ME', name: 'Montenegro', flag: '🇲🇪', dialCode: '+382' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦', dialCode: '+212' },
  { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', dialCode: '+258' },
  { code: 'MM', name: 'Myanmar', flag: '🇲🇲', dialCode: '+95' },
  { code: 'NA', name: 'Namibia', flag: '🇳🇦', dialCode: '+264' },
  { code: 'NR', name: 'Nauru', flag: '🇳🇷', dialCode: '+674' },
  { code: 'NP', name: 'Nepal', flag: '🇳🇵', dialCode: '+977' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dialCode: '+31' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', dialCode: '+505' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪', dialCode: '+227' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dialCode: '+234' },
  { code: 'KP', name: 'North Korea', flag: '🇰🇵', dialCode: '+850' },
  { code: 'MK', name: 'North Macedonia', flag: '🇲🇰', dialCode: '+389' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', dialCode: '+47' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '+968' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '+92' },
  { code: 'PW', name: 'Palau', flag: '🇵🇼', dialCode: '+680' },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸', dialCode: '+970' },
  { code: 'PA', name: 'Panama', flag: '🇵🇦', dialCode: '+507' },
  { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', dialCode: '+675' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾', dialCode: '+595' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪', dialCode: '+51' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', dialCode: '+63' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', dialCode: '+48' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '+351' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '+974' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴', dialCode: '+40' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼', dialCode: '+250' },
  { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳', dialCode: '+1' },
  { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨', dialCode: '+1' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', dialCode: '+1' },
  { code: 'WS', name: 'Samoa', flag: '🇼🇸', dialCode: '+685' },
  { code: 'SM', name: 'San Marino', flag: '🇸🇲', dialCode: '+378' },
  { code: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹', dialCode: '+239' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'SN', name: 'Senegal', flag: '🇸🇳', dialCode: '+221' },
  { code: 'RS', name: 'Serbia', flag: '🇷🇸', dialCode: '+381' },
  { code: 'SC', name: 'Seychelles', flag: '🇸🇨', dialCode: '+248' },
  { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', dialCode: '+232' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰', dialCode: '+421' },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮', dialCode: '+386' },
  { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧', dialCode: '+677' },
  { code: 'SO', name: 'Somalia', flag: '🇸🇴', dialCode: '+252' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
  { code: 'SS', name: 'South Sudan', flag: '🇸🇸', dialCode: '+211' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', dialCode: '+94' },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩', dialCode: '+249' },
  { code: 'SR', name: 'Suriname', flag: '🇸🇷', dialCode: '+597' },
  { code: 'SZ', name: 'Eswatini', flag: '🇸🇿', dialCode: '+268' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', dialCode: '+46' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dialCode: '+41' },
  { code: 'SY', name: 'Syria', flag: '🇸🇾', dialCode: '+963' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼', dialCode: '+886' },
  { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', dialCode: '+992' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', dialCode: '+255' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', dialCode: '+66' },
  { code: 'TL', name: 'Timor-Leste', flag: '🇹🇱', dialCode: '+670' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬', dialCode: '+228' },
  { code: 'TO', name: 'Tonga', flag: '🇹🇴', dialCode: '+676' },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹', dialCode: '+1' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳', dialCode: '+216' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90' },
  { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', dialCode: '+993' },
  { code: 'TV', name: 'Tuvalu', flag: '🇹🇻', dialCode: '+688' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬', dialCode: '+256' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦', dialCode: '+380' },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', dialCode: '+971' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾', dialCode: '+598' },
  { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', dialCode: '+998' },
  { code: 'VU', name: 'Vanuatu', flag: '🇻🇺', dialCode: '+678' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪', dialCode: '+58' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', dialCode: '+84' },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪', dialCode: '+967' },
  { code: 'ZM', name: 'Zambia', flag: '🇿🇲', dialCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', dialCode: '+263' },
];

// 🔥 Auth protection window: prevent state clearing for 30 seconds after auth initialization
const AUTH_PROTECTION_WINDOW = 30000; // 30 seconds

export const selectUILocale = (state: AppState): string =>
  // User-set override always wins; fall back to business locale from JWT, then English
  state.uiLocale || state.currentBusiness?.locale || 'en';

export const selectThemeMode = (state: AppState): 'light' | 'dark' => state.themeMode;

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      pendingEmail: '',
      isNewUser: false,
      phase1Token: null,
      countries: initialCountries,
      contactsRefreshToken: 0,
      uiLocale: 'en',
      themeMode: 'dark',
      leftPaneCollapsed: false,
      leftPaneWidth: PANE_DEFAULT_WIDTH,
      rightPaneCollapsed: false,
      rightPaneWidth: PANE_DEFAULT_WIDTH,

      // 🔥 NEW: Auth state protection
      isAuthInitializing: false,
      authInitializedAt: null,
      authSource: null,
      
      // Business state
      currentBusiness: null,
      userBusinesses: [],
      hasBusiness: false,
      isBusinessLoading: false,
      businessRole: null,
      
      // Subscription state
      currentSubscription: null,
      isSubscriptionLoading: false,
      
      // Actions
      setUser: (user) => {
        // DEBUG: Check if business exists BEFORE setUser
        const beforeState = get();
        console.log('🔍 [AppStore] BEFORE setUser - currentBusiness:', !!beforeState.currentBusiness, 'businessRole:', beforeState.businessRole);
        
        // Normalize user data: handle _id -> id mapping and ensure mobile/phone fields are preserved
        let normalizedUser: User | null = user;
        if (user) {
          const userWithId = user as User & { _id?: string };
          normalizedUser = {
            ...user,
            // Handle _id -> id mapping (backend may return _id)
            id: user.id || userWithId._id || user.id,
            // Ensure mobile and phone fields are preserved from API response
            mobile: user.mobile || user.phone || undefined,
            phone: user.phone || user.mobile || undefined,
            // Preserve preferences
            preferences: user.preferences,
          };
          // Remove _id if it exists to avoid confusion
          if (userWithId._id && normalizedUser.id) {
            delete (normalizedUser as any)._id;
          }
        }
        
        console.log('🔍 [AppStore] Setting user:', normalizedUser);
        console.log('🔍 [AppStore] User firstName:', normalizedUser?.firstName);
        console.log('🔍 [AppStore] User lastName:', normalizedUser?.lastName);
        console.log('🔍 [AppStore] User mobile:', normalizedUser?.mobile);
        console.log('🔍 [AppStore] User phone:', normalizedUser?.phone);
        
        set({ 
          user: normalizedUser, 
          isAuthenticated: !!normalizedUser 
        });
        
        // DEBUG: Check if business still exists AFTER setUser
        const afterState = get();
        console.log('🔍 [AppStore] AFTER setUser - currentBusiness:', !!afterState.currentBusiness, 'businessRole:', afterState.businessRole);
        
        // CRITICAL: Create backup after setting user (in case tokens were set first)
        const state = get();
        if (state.tokens?.token && normalizedUser) {
          AsyncStorage.setItem('@sendifier:auth:backup', JSON.stringify({
            token: state.tokens.token,
            refreshToken: state.tokens.refreshToken || '',
            user: normalizedUser,
            currentBusiness: state.currentBusiness,
            businessRole: state.businessRole
          })).then(() => {
            console.log('✅ [AppStore] Created backup after setUser');
          }).catch((error) => {
            console.error('❌ [AppStore] Failed to create backup:', error);
          });
        }
      },

      bumpContactsRefreshToken: () => {
        set((state) => ({ contactsRefreshToken: state.contactsRefreshToken + 1 }));
      },

      setUiLocale: (locale: string) => {
        set({ uiLocale: locale });
      },

      setThemeMode: (mode: 'light' | 'dark') => {
        set({ themeMode: mode });
      },

      setLeftPaneCollapsed: (collapsed: boolean) => {
        set({ leftPaneCollapsed: collapsed });
      },

      setLeftPaneWidth: (width: number) => {
        set({ leftPaneWidth: Math.min(PANE_MAX_WIDTH, Math.max(PANE_MIN_WIDTH, width)) });
      },

      setRightPaneCollapsed: (collapsed: boolean) => {
        set({ rightPaneCollapsed: collapsed });
      },

      setRightPaneWidth: (width: number) => {
        set({ rightPaneWidth: Math.min(PANE_MAX_WIDTH, Math.max(PANE_MIN_WIDTH, width)) });
      },

      setTokens: (tokens) => {
        set({ tokens });
        // Extract business data from token when tokens are set
        if (tokens?.token) {
          get().extractBusinessFromToken(tokens.token);
        }
        
        // CRITICAL: Create backup immediately after setting tokens
        if (tokens?.token) {
          const state = get();
          AsyncStorage.setItem('@sendifier:auth:backup', JSON.stringify({
            token: tokens.token,
            refreshToken: tokens.refreshToken || '',
            user: state.user,
            currentBusiness: state.currentBusiness,
            businessRole: state.businessRole
          })).then(() => {
            console.log('✅ [AppStore] Created backup after setTokens');
          }).catch((error) => {
            console.error('❌ [AppStore] Failed to create backup:', error);
          });
        }
      },
      
      updateTokens: (tokens) => {
        const currentTokens = get().tokens;
        const updatedTokens = currentTokens ? { ...currentTokens, ...tokens } : null;
        set({ tokens: updatedTokens });
        
        // Extract business data from updated token
        if (tokens.token || (updatedTokens?.token)) {
          const tokenToExtract = tokens.token || updatedTokens!.token;
          console.log('🔍 [AppStore] updateTokens: Extracting business from updated token');
          get().extractBusinessFromToken(tokenToExtract);
        }
      },
      
      setPendingEmail: (email) => set({ pendingEmail: email }),

      setIsNewUser: (isNew) => set({ isNewUser: isNew }),

      setPhase1Token: (token) => set({ phase1Token: token }),

      // Phase-1 tokens (phone/email/social login, before a business is selected) carry the
      // user's identity but no business context — the wizard/picker screens need `user`
      // populated (e.g. email for business-create defaults) immediately after verification,
      // not just the raw token. Mirrors extractBusinessFromToken's decode approach below.
      setUserFromPhase1Token: (token: string) => {
        try {
          const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
          const userFromToken: User = {
            id: payload.userId || payload.id,
            email: payload.email || '',
            firstName: payload.firstName || '',
            lastName: payload.lastName || '',
            username: payload.email ? payload.email.split('@')[0] : '',
            isEmailVerified: !!payload.emailVerified,
            isActive: true,
            mobile: payload.mobile || undefined,
            isMobileVerified: !!payload.mobileVerified,
            phone: payload.mobile || undefined,
            avatar: payload.avatar || undefined,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          set({ user: userFromToken });
        } catch (error) {
          console.error('[AppStore] Failed to decode phase-1 token for user:', error);
        }
      },
      
      logout: () => {
        // Clear API service tokens
        try {
          const { apiService } = require('../services/api');
          apiService.setAuthToken(null);
          apiService.setRefreshToken(null);
          console.log('✅ [AppStore] API service tokens cleared on logout');
        } catch (error) {
          console.error('❌ [AppStore] Error clearing API service tokens:', error);
        }
        
        // Clear store state
        set({ 
          user: null, 
          tokens: null, 
          isAuthenticated: false,
          pendingEmail: '',
          isNewUser: false,
          phase1Token: null,
          // 🔥 Clear auth state flags
          isAuthInitializing: false,
          authInitializedAt: null,
          authSource: null,
          // Clear business state
          currentBusiness: null,
          userBusinesses: [],
          hasBusiness: false,
          isBusinessLoading: false,
          businessRole: null,
          currentSubscription: null,
          isSubscriptionLoading: false
        });
        
        // Clear AsyncStorage backup ONLY on explicit logout
        AsyncStorage.removeItem('@sendifier:auth:backup').catch(err => {
          console.error('❌ [AppStore] Failed to clear backup on logout:', err);
        });
        console.log('✅ [AppStore] Backup cleared on explicit logout');
      },
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      // 🔥 NEW: Unified auth management functions
      
      /**
       * Validates that business state in store matches token
       * Auto-corrects if token has business but store doesn't
       */
      validateBusinessState: () => {
        const { tokens, currentBusiness } = get();
        
        if (!tokens?.token) {
          console.log('⚠️ [validateBusinessState] No token available');
          return false;
        }
        
        try {
          // Decode token to check business
          const tokenPayload = JSON.parse(decodeBase64Url(tokens.token.split('.')[1]));
          const tokenHasBusiness = !!(tokenPayload.businessId || tokenPayload.business?.id);
          const storeHasBusiness = !!currentBusiness;
          
          // Mismatch detected - token has business but store doesn't
          if (tokenHasBusiness && !storeHasBusiness) {
            console.warn('⚠️ [validateBusinessState] Token has business but store does not, extracting...');
            get().extractBusinessFromToken(tokens.token);
            return true;
          }
          
          // Store has business but token doesn't - might indicate outdated token
          if (!tokenHasBusiness && storeHasBusiness) {
            console.warn('⚠️ [validateBusinessState] Store has business but token does not - token might need refresh');
            // Don't clear business - token might need refresh
            return false;
          }
          
          console.log('✅ [validateBusinessState] Business state is valid');
          return tokenHasBusiness;
        } catch (error) {
          console.error('❌ [validateBusinessState] Error:', error);
          return false;
        }
      },
      
      /**
       * Atomic auth state setter - sets tokens, user, and extracts business in one operation
       * This is the MAIN function to use for login, OTP verification, and initial auth
       */
      setAuthState: async (authData: { tokens: AuthTokens; user: User; source: 'login' | 'rehydration' | 'token-refresh' }) => {
        console.log(`🔄 [setAuthState] Initializing auth from: ${authData.source}`);
        
        try {
          // 1. Lock auth updates to prevent race conditions
          set({ isAuthInitializing: true });
          
          // 2. Extract business from token BEFORE setting anything
          let business: Business | null = null;
          let businessRole: 'owner' | 'admin' | 'member' | null = null;
          
          if (authData.tokens.token) {
            try {
              const tokenPayload = JSON.parse(decodeBase64Url(authData.tokens.token.split('.')[1]));
              const businessId = tokenPayload.businessId || tokenPayload.business?.id;
              const role = tokenPayload.businessRole || tokenPayload.business?.role;
              const businessName = tokenPayload.businessName || tokenPayload.business?.name;
              
              if (businessId && role) {
                business = {
                  _id: businessId,
                  name: businessName || 'Business',
                  email: tokenPayload.businessEmail || tokenPayload.business?.email || '',
                  phone: tokenPayload.businessPhone || tokenPayload.business?.phone || '',
                  address: tokenPayload.businessAddress || tokenPayload.business?.address || '',
                  city: tokenPayload.businessCity || tokenPayload.business?.city || '',
                  state: tokenPayload.businessState || tokenPayload.business?.state || '',
                  country: tokenPayload.businessCountry || tokenPayload.business?.country || '',
                  postalCode: tokenPayload.businessPostalCode || tokenPayload.business?.postalCode || '',
                  website: tokenPayload.businessWebsite || tokenPayload.business?.website || '',
                  description: tokenPayload.businessDescription || tokenPayload.business?.description || '',
                  industry: tokenPayload.businessIndustry || tokenPayload.business?.industry || '',
                  size: tokenPayload.businessSize || tokenPayload.business?.size || '',
                  logo: tokenPayload.businessLogo || tokenPayload.business?.logo || '',
                  isVerified: tokenPayload.businessVerified || tokenPayload.business?.isVerified || false,
                  verificationDate: tokenPayload.businessVerificationDate || tokenPayload.business?.verificationDate || '',
                  timezone: tokenPayload.businessTimezone || tokenPayload.business?.timezone || '',
                  locale: tokenPayload.businessLocale || tokenPayload.business?.locale || '',
                  currency: tokenPayload.businessCurrency || tokenPayload.business?.currency || '',
                  ownerId: tokenPayload.businessOwnerId || tokenPayload.business?.ownerId || '',
                  members: tokenPayload.businessMembers || tokenPayload.business?.members || [],
                  departments: tokenPayload.businessDepartments || tokenPayload.business?.departments || [],
                  subscriptionPlan: tokenPayload.businessSubscriptionPlan || tokenPayload.subscription?.planCode || '',
                  subscriptionStatus: tokenPayload.businessSubscriptionStatus || (tokenPayload.subscription?.status === 1 ? 'active' : 'inactive'),
                  subscriptionExpiry: tokenPayload.businessSubscriptionExpiry || tokenPayload.subscription?.currentPeriodEnd || '',
                  maxUsers: tokenPayload.businessMaxUsers || tokenPayload.subscription?.services?.USER?.number_of_users || 5,
                  maxChannels: tokenPayload.businessMaxChannels || tokenPayload.subscription?.services?.wabu?.number_of_whatsapp_numbers || 1,
                  tags: tokenPayload.businessTags || tokenPayload.business?.tags || [],
                  notes: tokenPayload.businessNotes || tokenPayload.business?.notes || '',
                  customFields: tokenPayload.businessCustomFields || tokenPayload.business?.customFields || {},
                  status: 1,
                  createdAt: tokenPayload.businessCreatedAt || tokenPayload.business?.createdAt || new Date().toISOString(),
                  updatedAt: tokenPayload.businessUpdatedAt || tokenPayload.business?.updatedAt || new Date().toISOString()
                };
                businessRole = role as 'owner' | 'admin' | 'member';
                console.log('✅ [setAuthState] Extracted business from token:', business._id, business.name);
              } else {
                console.log('ℹ️ [setAuthState] No business context in token');
              }
            } catch (extractError) {
              console.error('❌ [setAuthState] Error extracting business:', extractError);
            }
          }
          
          // 3. Set everything atomically
          set({
            tokens: authData.tokens,
            user: authData.user,
            currentBusiness: business,
            hasBusiness: !!business,
            businessRole: businessRole,
            isAuthenticated: true,
            authInitializedAt: Date.now(),
            authSource: authData.source,
            isAuthInitializing: false
          });
          
          console.log('✅ [setAuthState] Auth state set atomically', {
            hasUser: !!authData.user,
            hasTokens: !!authData.tokens.token,
            hasBusiness: !!business,
            businessRole: businessRole,
            source: authData.source
          });
          
          // 4. Sync tokens to API service immediately
          try {
            const { syncApiServiceWithStore } = require('../services/api');
            syncApiServiceWithStore({
              token: authData.tokens.token,
              refreshToken: authData.tokens.refreshToken || ''
            });
            console.log('✅ [setAuthState] Tokens synced to API service');
          } catch (syncError) {
            console.error('❌ [setAuthState] Failed to sync tokens to API service:', syncError);
          }
          
          // 5. Create backup in AsyncStorage
          try {
            await AsyncStorage.setItem('@sendifier:auth:backup', JSON.stringify({
              token: authData.tokens.token,
              refreshToken: authData.tokens.refreshToken || '',
              user: authData.user,
              currentBusiness: business,
              businessRole: businessRole
            }));
            console.log('✅ [setAuthState] Auth backup created');
          } catch (backupError) {
            console.error('❌ [setAuthState] Failed to create backup:', backupError);
          }
        } catch (error) {
          console.error('❌ [setAuthState] Error:', error);
          set({ isAuthInitializing: false });
          throw error;
        }
      },
      
      /**
       * Updates auth tokens (for business creation, service setup, token refresh)
       * Extracts business from new token and merges with existing data
       */
      updateAuthTokens: async (newTokens: AuthTokens, source: 'business-creation' | 'service-setup' | 'token-refresh') => {
        console.log(`🔄 [updateAuthTokens] Updating tokens from: ${source}`);
        
        try {
          // 1. Validate token format before processing
          if (newTokens.token) {
            const tokenParts = newTokens.token.split('.');
            if (tokenParts.length !== 3) {
              console.error(`❌ [updateAuthTokens] Invalid token format from ${source}:`, {
                tokenLength: newTokens.token.length,
                partsCount: tokenParts.length,
                tokenPreview: newTokens.token.substring(0, 50),
                hasDots: newTokens.token.includes('.'),
                dotCount: (newTokens.token.match(/\./g) || []).length
              });
              throw new Error(`Invalid token format: expected 3 parts, got ${tokenParts.length}`);
            }
            console.log(`✅ [updateAuthTokens] Token format validated: ${tokenParts.length} parts`);
          }
          
          // 2. Lock auth updates
          set({ isAuthInitializing: true });
          
          // 3. Extract business from new token
          let newBusiness: Business | null = null;
          let newBusinessRole: 'owner' | 'admin' | 'member' | null = null;
          
          if (newTokens.token) {
            try {
              // Validate token parts before decoding
              const tokenParts = newTokens.token.split('.');
              if (tokenParts.length !== 3 || !tokenParts[1]) {
                throw new Error('Invalid token format for decoding');
              }
              
              const tokenPayload = JSON.parse(decodeBase64Url(tokenParts[1]));
              const businessId = tokenPayload.businessId || tokenPayload.business?.id;
              const role = tokenPayload.businessRole || tokenPayload.business?.role;
              const businessName = tokenPayload.businessName || tokenPayload.business?.name;
              
              if (businessId && role) {
                newBusiness = {
                  _id: businessId,
                  name: businessName || 'Business',
                  email: tokenPayload.businessEmail || tokenPayload.business?.email || '',
                  phone: tokenPayload.businessPhone || tokenPayload.business?.phone || '',
                  address: tokenPayload.businessAddress || tokenPayload.business?.address || '',
                  city: tokenPayload.businessCity || tokenPayload.business?.city || '',
                  state: tokenPayload.businessState || tokenPayload.business?.state || '',
                  country: tokenPayload.businessCountry || tokenPayload.business?.country || '',
                  postalCode: tokenPayload.businessPostalCode || tokenPayload.business?.postalCode || '',
                  website: tokenPayload.businessWebsite || tokenPayload.business?.website || '',
                  description: tokenPayload.businessDescription || tokenPayload.business?.description || '',
                  industry: tokenPayload.businessIndustry || tokenPayload.business?.industry || '',
                  size: tokenPayload.businessSize || tokenPayload.business?.size || '',
                  logo: tokenPayload.businessLogo || tokenPayload.business?.logo || '',
                  isVerified: tokenPayload.businessVerified || tokenPayload.business?.isVerified || false,
                  verificationDate: tokenPayload.businessVerificationDate || tokenPayload.business?.verificationDate || '',
                  timezone: tokenPayload.businessTimezone || tokenPayload.business?.timezone || '',
                  locale: tokenPayload.businessLocale || tokenPayload.business?.locale || '',
                  currency: tokenPayload.businessCurrency || tokenPayload.business?.currency || '',
                  ownerId: tokenPayload.businessOwnerId || tokenPayload.business?.ownerId || '',
                  members: tokenPayload.businessMembers || tokenPayload.business?.members || [],
                  departments: tokenPayload.businessDepartments || tokenPayload.business?.departments || [],
                  subscriptionPlan: tokenPayload.businessSubscriptionPlan || tokenPayload.subscription?.planCode || '',
                  subscriptionStatus: tokenPayload.businessSubscriptionStatus || (tokenPayload.subscription?.status === 1 ? 'active' : 'inactive'),
                  subscriptionExpiry: tokenPayload.businessSubscriptionExpiry || tokenPayload.subscription?.currentPeriodEnd || '',
                  maxUsers: tokenPayload.businessMaxUsers || tokenPayload.subscription?.services?.USER?.number_of_users || 5,
                  maxChannels: tokenPayload.businessMaxChannels || tokenPayload.subscription?.services?.wabu?.number_of_whatsapp_numbers || 1,
                  tags: tokenPayload.businessTags || tokenPayload.business?.tags || [],
                  notes: tokenPayload.businessNotes || tokenPayload.business?.notes || '',
                  customFields: tokenPayload.businessCustomFields || tokenPayload.business?.customFields || {},
                  status: 1,
                  createdAt: tokenPayload.businessCreatedAt || tokenPayload.business?.createdAt || new Date().toISOString(),
                  updatedAt: tokenPayload.businessUpdatedAt || tokenPayload.business?.updatedAt || new Date().toISOString()
                };
                newBusinessRole = role as 'owner' | 'admin' | 'member';
                console.log('✅ [updateAuthTokens] Extracted business from new token:', newBusiness._id, newBusiness.name);
              }
            } catch (extractError) {
              console.error('❌ [updateAuthTokens] Error extracting business:', extractError);
            }
          }
          
          // 3. Validate token again before storing
          if (newTokens.token) {
            const finalTokenParts = newTokens.token.split('.');
            if (finalTokenParts.length !== 3) {
              console.error(`❌ [updateAuthTokens] Token corrupted before storing:`, {
                partsCount: finalTokenParts.length,
                tokenLength: newTokens.token.length,
                tokenPreview: newTokens.token.substring(0, 50)
              });
              throw new Error('Token validation failed before storage');
            }
          }
          
          // 4. Get current state
          const currentState = get();
          
          // 5. Merge business data (new token takes precedence)
          const finalBusiness = newBusiness || currentState.currentBusiness;
          const finalBusinessRole = newBusinessRole || currentState.businessRole;
          
          // 6. Update atomically
          console.log(`✅ [updateAuthTokens] Storing tokens:`, {
            tokenLength: newTokens.token?.length || 0,
            tokenParts: newTokens.token ? newTokens.token.split('.').length : 0,
            refreshTokenLength: newTokens.refreshToken?.length || 0
          });
          
          set({
            tokens: newTokens,
            currentBusiness: finalBusiness,
            hasBusiness: !!finalBusiness,
            businessRole: finalBusinessRole,
            authInitializedAt: Date.now(),
            authSource: source === 'token-refresh' ? 'token-refresh' : currentState.authSource,
            isAuthInitializing: false
          });
          
          // 7. Verify token after storing
          const storedState = get();
          if (storedState.tokens?.token) {
            const storedTokenParts = storedState.tokens.token.split('.');
            if (storedTokenParts.length !== 3) {
              console.error(`❌ [updateAuthTokens] Token corrupted after storing:`, {
                partsCount: storedTokenParts.length,
                tokenLength: storedState.tokens.token.length
              });
            } else {
              console.log(`✅ [updateAuthTokens] Token verified after storing: ${storedTokenParts.length} parts`);
            }
          }
          
          console.log('✅ [updateAuthTokens] Tokens updated', {
            hasBusiness: !!finalBusiness,
            businessId: finalBusiness?._id,
            businessName: finalBusiness?.name,
            businessRole: finalBusinessRole,
            source
          });
          
          // 6. Update backup
          if (currentState.user) {
            try {
              await AsyncStorage.setItem('@sendifier:auth:backup', JSON.stringify({
                token: newTokens.token,
                refreshToken: newTokens.refreshToken || '',
                user: currentState.user,
                currentBusiness: finalBusiness,
                businessRole: finalBusinessRole
              }));
              console.log('✅ [updateAuthTokens] Backup updated');
            } catch (backupError) {
              console.error('❌ [updateAuthTokens] Failed to update backup:', backupError);
            }
          }
        } catch (error) {
          console.error('❌ [updateAuthTokens] Error:', error);
          set({ isAuthInitializing: false });
          throw error;
        }
      },
      
      // Business actions
      setCurrentBusiness: (business) => {
        console.log('🔍 [AppStore] Setting current business:', business);
        set({ 
          currentBusiness: business,
          hasBusiness: !!business
        });
      },
      
      setUserBusinesses: (businesses) => {
        console.log('🔍 [AppStore] Setting user businesses:', businesses);
        set({ 
          userBusinesses: businesses,
          hasBusiness: businesses.length > 0,
          currentBusiness: businesses.length > 0 ? businesses[0] : null
        });
      },
      
      setBusinessLoading: (loading) => set({ isBusinessLoading: loading }),
      
      extractBusinessFromToken: (token: string) => {
        try {
          console.log('🔍 [AppStore] Extracting business from token...');
          console.log('🔍 [AppStore] Token length:', token.length);
          
          // Decode JWT token
          const tokenPayload = JSON.parse(decodeBase64Url(token.split('.')[1]));
          console.log('🔍 [AppStore] Token payload:', tokenPayload);
          console.log('🔍 [AppStore] Business ID in token (flat):', tokenPayload.businessId);
          console.log('🔍 [AppStore] Business ID in token (nested):', tokenPayload.business?.id);
          console.log('🔍 [AppStore] Business role in token (flat):', tokenPayload.businessRole);
          console.log('🔍 [AppStore] Business role in token (nested):', tokenPayload.business?.role);
          
          // Check if token contains business information (both flat and nested structures)
          const businessId = tokenPayload.businessId || tokenPayload.business?.id;
          const businessRole = tokenPayload.businessRole || tokenPayload.business?.role;
          const businessName = tokenPayload.businessName || tokenPayload.business?.name;
          
          if (businessId && businessRole) {
            console.log('✅ [AppStore] Found business in token:', businessId);
            
            // Create business object from token payload (handle both flat and nested structures)
            const businessFromToken: Business = {
              _id: businessId,
              name: businessName || 'Business',
              email: tokenPayload.businessEmail || tokenPayload.business?.email || '',
              phone: tokenPayload.businessPhone || tokenPayload.business?.phone || '',
              address: tokenPayload.businessAddress || tokenPayload.business?.address || '',
              city: tokenPayload.businessCity || tokenPayload.business?.city || '',
              state: tokenPayload.businessState || tokenPayload.business?.state || '',
              country: tokenPayload.businessCountry || tokenPayload.business?.country || '',
              postalCode: tokenPayload.businessPostalCode || tokenPayload.business?.postalCode || '',
              website: tokenPayload.businessWebsite || tokenPayload.business?.website || '',
              description: tokenPayload.businessDescription || tokenPayload.business?.description || '',
              industry: tokenPayload.businessIndustry || tokenPayload.business?.industry || '',
              size: tokenPayload.businessSize || tokenPayload.business?.size || '',
              logo: tokenPayload.businessLogo || tokenPayload.business?.logo || '',
              isVerified: tokenPayload.businessVerified || tokenPayload.business?.isVerified || false,
              verificationDate: tokenPayload.businessVerificationDate || tokenPayload.business?.verificationDate || '',
              timezone: tokenPayload.businessTimezone || tokenPayload.business?.timezone || '',
              locale: tokenPayload.businessLocale || tokenPayload.business?.locale || '',
              currency: tokenPayload.businessCurrency || tokenPayload.business?.currency || '',
              ownerId: tokenPayload.businessOwnerId || tokenPayload.business?.ownerId || '',
              members: tokenPayload.businessMembers || tokenPayload.business?.members || [],
              departments: tokenPayload.businessDepartments || tokenPayload.business?.departments || [],
              subscriptionPlan: tokenPayload.businessSubscriptionPlan || tokenPayload.subscription?.planCode || '',
              subscriptionStatus: tokenPayload.businessSubscriptionStatus || (tokenPayload.subscription?.status === 1 ? 'active' : 'inactive'),
              subscriptionExpiry: tokenPayload.businessSubscriptionExpiry || tokenPayload.subscription?.currentPeriodEnd || '',
              maxUsers: tokenPayload.businessMaxUsers || tokenPayload.subscription?.services?.USER?.number_of_users || 5,
              maxChannels: tokenPayload.businessMaxChannels || tokenPayload.subscription?.services?.wabu?.number_of_whatsapp_numbers || 1,
              tags: tokenPayload.businessTags || tokenPayload.business?.tags || [],
              notes: tokenPayload.businessNotes || tokenPayload.business?.notes || '',
              customFields: tokenPayload.businessCustomFields || tokenPayload.business?.customFields || {},
              status: 1,
              createdAt: tokenPayload.businessCreatedAt || tokenPayload.business?.createdAt || new Date().toISOString(),
              updatedAt: tokenPayload.businessUpdatedAt || tokenPayload.business?.updatedAt || new Date().toISOString()
            };
            
            // Set the business in the store
            set({ 
              currentBusiness: businessFromToken,
              userBusinesses: [businessFromToken],
              hasBusiness: true,
              isBusinessLoading: false,
              businessRole: businessRole as 'owner' | 'admin' | 'member'
            });
            
            console.log('✅ [AppStore] Business extracted and set:', businessFromToken.name);
          } else {
            console.log('⚠️ [AppStore] No business context in token');
            set({ 
              currentBusiness: null,
              userBusinesses: [],
              hasBusiness: false,
              isBusinessLoading: false,
              businessRole: null
            });
          }
        } catch (error) {
          console.error('❌ [AppStore] Error extracting business from token:', error);
          set({ 
            currentBusiness: null,
            userBusinesses: [],
            hasBusiness: false,
            isBusinessLoading: false
          });
        }
      },

      checkUserBusinesses: async () => {
        const { tokens, isBusinessLoading, authInitializedAt, currentBusiness, businessRole } = get();
        
        // 🔥 CRITICAL PROTECTION: If auth was recently initialized, don't overwrite business
        if (authInitializedAt && Date.now() - authInitializedAt < AUTH_PROTECTION_WINDOW) {
          if (currentBusiness) {
            console.log('🔒 [checkUserBusinesses] Auth recently initialized, business exists - skipping API call');
            return true;
          }
        }
        
        // 🔥 CRITICAL: If business already exists, don't overwrite unless explicitly checking
        if (currentBusiness && businessRole) {
          console.log('✅ [checkUserBusinesses] Business already exists in state - skipping API call');
          return true;
        }
        
        // ⚠️ CRITICAL: Capture business state FIRST, BEFORE any async operations or set() calls!
        // Zustand's persist middleware is ASYNC and can clear/overwrite state during API calls
        // DEEP CLONE to prevent any mutations from affecting the captured value
        const capturedBusiness = currentBusiness ? JSON.parse(JSON.stringify(currentBusiness)) : null;
        const capturedRole = businessRole;
        
        console.log('🔍 [AppStore] checkUserBusinesses - CAPTURED:', !!capturedBusiness, 'capturedBusiness._id:', capturedBusiness?._id, 'role:', capturedRole);
        
        // Prevent multiple simultaneous calls
        if (isBusinessLoading) {
          console.log('⚠️ [AppStore] Business check already in progress, skipping...');
          return !!currentBusiness;
        }
        
        if (!tokens?.token) {
          console.log('❌ [AppStore] No token available for business check');
          return false;
        }
        
        try {
          set({ isBusinessLoading: true });
          
          console.log('🔍 [AppStore] Checking user businesses via API...');
          
          // Import apiService dynamically to avoid circular dependency
          const { apiService } = await import('../services/api');
          const response = await apiService.getBusinesses();
          
          console.log('🔍 [AppStore] API response - statusCode:', response.statusCode, 'data length:', response.data?.length);
          
          if (response.statusCode === 1000 && response.data && Array.isArray(response.data)) {
            const businesses = response.data;
            const hasBusinesses = businesses.length > 0;
            console.log('✅ [AppStore] Found businesses:', businesses.length, 'hasBusinesses:', hasBusinesses);
            console.log('✅ [AppStore] Using CAPTURED state - business:', !!capturedBusiness, 'role:', capturedRole);
            
            // Use captured business if exists, otherwise use first business from API
            const finalBusiness = capturedBusiness || (hasBusinesses ? businesses[0] : null);
            
            set({ 
              userBusinesses: businesses,
              hasBusiness: hasBusinesses || !!capturedBusiness,
              // 🔥 Use CAPTURED values to preserve existing business
              currentBusiness: finalBusiness,
              businessRole: capturedRole,
              isBusinessLoading: false
            });
            
            console.log('✅ [AppStore] Business state SET - currentBusiness:', !!finalBusiness, 'currentBusiness._id:', finalBusiness?._id, 'currentBusiness.name:', finalBusiness?.name, 'businessRole:', capturedRole);
            
            return hasBusinesses || !!capturedBusiness;
          } else {
            console.warn('⚠️ [AppStore] API returned no businesses, but preserving existing state');
            // 🔥 NEVER clear existing business on API error
            set({ 
              userBusinesses: [],
              isBusinessLoading: false
              // DON'T set currentBusiness: null, hasBusiness: false - preserve existing state
            });
            return !!capturedBusiness;
          }
        } catch (error: any) {
          console.error('❌ [AppStore] Error checking user businesses:', error);
          
          // Check if this is an authentication error
          const errorMessage = error?.message || error?.toString() || '';
          const isAuthError = errorMessage.includes('Authentication failed') || 
                             errorMessage.includes('token expired') ||
                             errorMessage.includes('Please login again') ||
                             errorMessage.includes('Unauthorized');
          
          // 🔥 NEVER clear existing business on error - only clear loading flag
          set({ 
            isBusinessLoading: false
            // DON'T clear currentBusiness, hasBusiness, businessRole - preserve existing state
          });
          
          console.warn('⚠️ [checkUserBusinesses] Error occurred but preserving existing business state');
          
          // Re-throw authentication errors so they can be properly handled
          if (isAuthError) {
            console.log('🔄 [AppStore] Re-throwing authentication error for proper handling');
            throw error;
          }
          
          // Return current business state instead of false
          return !!capturedBusiness;
        }
      },
      
      initializeTokenManager: () => {
        // Register callbacks with TokenManager
        tokenManager.registerCallback({
          onTokenRefreshed: async (newToken: string, newRefreshToken: string) => {
            console.log('🔄 [AppStore] Tokens refreshed, updating store...');
            
            // Update tokens in store
            set({
              tokens: {
                token: newToken,
                refreshToken: newRefreshToken
              }
            });
            
            // Extract business and subscription data from new token
            const state = get();
            if (newToken) {
              try {
                state.extractBusinessFromToken(newToken);
                console.log('✅ [AppStore] Business data extracted from refreshed token');
              } catch (extractError) {
                console.error('❌ [AppStore] Error extracting business from refreshed token:', extractError);
              }
            }
            
            // Update AsyncStorage backup
            try {
              const currentState = get();
              await AsyncStorage.setItem('@sendifier:auth:backup', JSON.stringify({
                token: newToken,
                refreshToken: newRefreshToken,
                user: currentState.user,
                currentBusiness: currentState.currentBusiness,
                businessRole: currentState.businessRole
              }));
              console.log('✅ [AppStore] Backup updated after token refresh');
            } catch (backupError) {
              console.error('❌ [AppStore] Failed to update backup after token refresh:', backupError);
            }
            
            console.log('✅ [AppStore] Store updated with refreshed tokens');
          },
          onTokenRefreshFailed: (error: string) => {
            console.log('❌ [AppStore] Token refresh failed:', error);
            // Clear tokens and set user as unauthenticated
            set({
              tokens: null,
              user: null,
              isAuthenticated: false,
              currentBusiness: null,
              userBusinesses: [],
              hasBusiness: false,
              businessRole: null,
              currentSubscription: null
            });
            
            // DON'T clear AsyncStorage backup on token refresh failure
            // The backup should be preserved so user can restore their session
            // Only clear backup on explicit logout
            console.log('⚠️ [AppStore] Token refresh failed but keeping backup for session recovery');
          }
        });
        console.log('✅ [AppStore] TokenManager callback registered');
      },
      
      // Subscription actions
      setCurrentSubscription: (subscription) => {
        console.log('🔍 [AppStore] Setting current subscription:', subscription);
        set({ currentSubscription: subscription });
      },
      
      setSubscriptionLoading: (loading) => set({ isSubscriptionLoading: loading }),
      
      loadCurrentSubscription: async () => {
        const { currentBusiness, tokens, isSubscriptionLoading } = get();
        
        // Prevent multiple simultaneous calls
        if (isSubscriptionLoading) {
          console.log('⚠️ [AppStore] Subscription loading already in progress, skipping...');
          return;
        }
        
        if (!currentBusiness) {
          console.log('⚠️ [AppStore] No current business available for subscription loading');
          return;
        }
        
        if (!tokens?.token) {
          console.log('⚠️ [AppStore] No token available, creating fallback subscription from business data');
          // Create fallback subscription even without token
          const fallbackSubscription: Subscription = {
            id: currentBusiness._id,
            planId: currentBusiness.subscriptionPlan || 'starter',
            planCode: currentBusiness.subscriptionPlan || 'starter',
            status: currentBusiness.subscriptionStatus || 'active',
            billingCycle: 'monthly',
            currentPeriodStart: new Date().toISOString(),
            currentPeriodEnd: currentBusiness.subscriptionExpiry || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            services: {
              wabu: {
                number_of_whatsapp_numbers: 3, // Correct value from subscription
                number_of_message_initiate: 500,
                number_of_message_receive: 1000,
                text_messages: true,
                image_messages: true,
                video_messages: true,
                audio_messages: true,
                document_messages: true,
                template_messages: true,
                interactive_messages: true
              },
              fbm: {
                number_of_facebook_pages: 2, // Correct value from subscription
                number_of_message_initiate: 1000, // Fixed: should be 1000 for Facebook
                number_of_message_receive: 2000, // Fixed: should be 2000 for Facebook
                text_messages: true,
                image_messages: true,
                video_messages: true,
                audio_messages: true,
                document_messages: true,
                template_messages: true,
                interactive_messages: true,
                quick_replies: true,
                buttons: true,
                lists: true,
                carousels: true
              },
              users: {
                max_users: currentBusiness.maxUsers || 5
              },
              departments: {
                max_departments: 3
              },
              storage: {
                max_storage_gb: 10
              }
            }
          };
          
          set({ 
            currentSubscription: fallbackSubscription,
            isSubscriptionLoading: false
          });
          return;
        }
        
        try {
          set({ isSubscriptionLoading: true });
          console.log('🔍 [AppStore] Loading current subscription...');
          
          // Import apiService dynamically to avoid circular dependency
          const { apiService } = await import('../services/api');
          
          // Always try to get fresh subscription data from API first
          let subscriptionResponse;
          try {
            // Use business ID to get subscription data
            const businessId = currentBusiness._id;
            
            console.log('🔍 [AppStore] Loading fresh subscription data from API using business ID:', businessId);
            console.log('🔍 [AppStore] Current business:', currentBusiness);
            
            if (businessId) {
              subscriptionResponse = await apiService.getBusinessSubscriptions(businessId);
              
              if (subscriptionResponse.statusCode === 1000 && subscriptionResponse.data && subscriptionResponse.data.length > 0) {
                console.log('✅ [AppStore] Successfully loaded fresh subscription data from API');
                // Get the first (most recent) subscription
                const subscriptionData = subscriptionResponse.data[0];
                
                // Map backend numeric status to mobile string status
                // 0: inactive -> 'pending', 1: active -> 'active', 2: suspended -> 'cancelled', 3: expired -> 'expired'
                const statusMap: { [key: number]: 'active' | 'expired' | 'cancelled' | 'pending' } = {
                  0: 'pending',
                  1: 'active',
                  2: 'cancelled',
                  3: 'expired'
                };
                
                const subscription: Subscription = {
                  id: subscriptionData._id,
                  planId: subscriptionData.planId,
                  planCode: subscriptionData.planCode || currentBusiness.subscriptionPlan,
                  status: statusMap[subscriptionData.status] || 'pending',
                  billingCycle: subscriptionData.billingCycle || 'monthly',
                  currentPeriodStart: subscriptionData.currentPeriodStart,
                  currentPeriodEnd: subscriptionData.currentPeriodEnd,
                  services: subscriptionData.services || {}
                };
                
                console.log('🔍 [AppStore] Fresh subscription data loaded:', subscription);
                set({ 
                  currentSubscription: subscription,
                  isSubscriptionLoading: false
                });
                return;
              }
            }
            
            console.log('⚠️ [AppStore] No subscription ID found or API call failed, using fallback');
            throw new Error('No subscription ID available or API call failed');
          } catch (error) {
            console.log('⚠️ [AppStore] Could not load fresh subscription data, using user subscription data or fallback');
            
            // Try to use subscription data from user object as fallback
            const user = get().user;
            const userSubscription = (user as any)?.subscription;
            
            if (userSubscription) {
              console.log('🔍 [AppStore] Using subscription data from user object as fallback');
              set({ 
                currentSubscription: userSubscription,
                isSubscriptionLoading: false
              });
              return;
            }
            
            // Fallback to creating subscription from business data
            const fallbackSubscription: Subscription = {
              id: currentBusiness._id,
              planId: currentBusiness.subscriptionPlan || 'starter',
              planCode: currentBusiness.subscriptionPlan || 'starter',
              status: currentBusiness.subscriptionStatus || 'active',
              billingCycle: 'monthly',
              currentPeriodStart: new Date().toISOString(),
              currentPeriodEnd: currentBusiness.subscriptionExpiry || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              services: {
                wabu: {
                  number_of_whatsapp_numbers: 3, // Correct value from subscription
                  number_of_message_initiate: 0, // Fixed: was 1000, should be 500
                  number_of_message_receive: 0, // Fixed: was 2000, should be 500
                  text_messages: true,
                  image_messages: true,
                  video_messages: true,
                  audio_messages: true,
                  document_messages: true,
                  template_messages: true,
                  interactive_messages: true
                },
                fbm: {
                  number_of_facebook_pages: 2, // Correct value from subscription
                  number_of_message_initiate: 0,
                  number_of_message_receive: 0,
                  text_messages: true,
                  image_messages: true,
                  video_messages: true,
                  audio_messages: true,
                  document_messages: true,
                  template_messages: true,
                  interactive_messages: true,
                  quick_replies: true,
                  buttons: true,
                  lists: true,
                  carousels: true
                },
                users: {
                  max_users: currentBusiness.maxUsers || 5
                },
                departments: {
                  max_departments: 3
                },
                storage: {
                  max_storage_gb: 10
                }
              }
            };
            
            set({ 
              currentSubscription: fallbackSubscription,
              isSubscriptionLoading: false
            });
            return;
          }
        } catch (error) {
          console.error('❌ [AppStore] Error loading subscription:', error);
          set({ 
            currentSubscription: null,
            isSubscriptionLoading: false
          });
        }
      },
    }),
    {
      name: 'sendifier-app-store',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        pendingEmail: state.pendingEmail,
        isNewUser: state.isNewUser,
        phase1Token: state.phase1Token,
        // 🔥 NEW: Persist auth state protection flags
        authInitializedAt: state.authInitializedAt,
        authSource: state.authSource,
        // Business state
        currentBusiness: state.currentBusiness,
        userBusinesses: state.userBusinesses,
        hasBusiness: state.hasBusiness,
        businessRole: state.businessRole, // Persist business role so it survives navigation/refresh
        currentSubscription: state.currentSubscription,
        // User language preference — persists across sessions
        uiLocale: state.uiLocale,
        // User theme preference — persists across sessions
        themeMode: state.themeMode,
        // Desktop-web pane collapse/resize state — persists across sessions
        leftPaneCollapsed: state.leftPaneCollapsed,
        leftPaneWidth: state.leftPaneWidth,
        rightPaneCollapsed: state.rightPaneCollapsed,
        rightPaneWidth: state.rightPaneWidth,
      }),
      onRehydrateStorage: () => {
        console.log('🔄 [AppStore] Starting rehydration...');
        return async (state, error) => {
          if (error) {
            console.error('❌ [AppStore] Rehydration error:', error);
            return;
          }
          
          if (state) {
            const currentState = useAppStore.getState();
            
            // 🔥 CRITICAL PROTECTION 1: Don't rehydrate during active auth initialization
            if (currentState.isAuthInitializing) {
              console.log('🔒 [AppStore] Auth is currently initializing, skipping rehydration to prevent race condition');
              return;
            }
            
            // 🔥 CRITICAL PROTECTION 2: Don't rehydrate if auth was recently initialized
            if (currentState.authInitializedAt && Date.now() - currentState.authInitializedAt < AUTH_PROTECTION_WINDOW) {
              console.log('🔒 [AppStore] Auth recently initialized, skipping rehydration', {
                timeSinceInit: Date.now() - currentState.authInitializedAt,
                protectionWindow: AUTH_PROTECTION_WINDOW
              });
              return;
            }
            
            // CRITICAL: If we have tokens/business but rehydration would clear them, skip rehydration
            // Protect tokens from being cleared
            if (currentState.tokens && !state.tokens) {
              console.log('⚠️ [AppStore] Rehydration would clear tokens, keeping current state');
              return; // Don't apply rehydrated state if it would clear our tokens
            }
            
            // Protect currentBusiness from being cleared
            if (currentState.currentBusiness && !state.currentBusiness) {
              console.log('⚠️ [AppStore] Rehydration would clear currentBusiness, keeping current state');
              return; // Don't apply rehydrated state if it would clear our business
            }
            
            // Protect businessRole from being cleared
            if (currentState.businessRole && !state.businessRole) {
              console.log('⚠️ [AppStore] Rehydration would clear businessRole, keeping current state');
              return; // Don't apply rehydrated state if it would clear our business role
            }
            
            // CRITICAL: If rehydrated state is empty, restore from backup
            if (!state.tokens?.token && !state.user) {
              console.log('⚠️ [AppStore] Rehydrated state is EMPTY - attempting to restore from backup...');
              
              try {
                const backupStr = await AsyncStorage.getItem('@sendifier:auth:backup');
                
                if (backupStr) {
                  const backup = JSON.parse(backupStr);
                  
                  // Restore all state from backup
                  state.tokens = { token: backup.token, refreshToken: backup.refreshToken };
                  state.user = backup.user;
                  state.currentBusiness = backup.currentBusiness;
                  state.businessRole = backup.businessRole;
                  state.isAuthenticated = true;
                  state.hasBusiness = !!backup.currentBusiness;
                  
                  console.log('✅ [AppStore] State restored from backup successfully!', {
                    hasUser: !!state.user,
                    hasTokens: !!state.tokens?.token,
                    hasBusiness: !!state.currentBusiness,
                    businessRole: state.businessRole,
                    currentBusinessId: state.currentBusiness?._id
                  });
                } else {
                  console.log('ℹ️ [AppStore] No backup found (expected on first login or after logout)');
                }
              } catch (backupError) {
                console.error('❌ [AppStore] Failed to restore from backup:', backupError);
              }
            }
          }
          
          console.log('✅ [AppStore] Rehydration complete');
          
          // If we have tokens but no businessRole, extract it from token
          if (state?.tokens?.token && !state.businessRole) {
            console.log('🔄 [AppStore] Token exists but no businessRole, extracting from token...');
            state.extractBusinessFromToken(state.tokens.token);
          } else if (state?.businessRole) {
            console.log('✅ [AppStore] BusinessRole already persisted:', state.businessRole);
          }
        };
      },
    }
  )
);
